/**
 * **拡張帯**（timing v4 の `bands`。Issue #914。仕様は `docs/duel-timing.md`「拡張帯」）。
 *
 * 人が置く・種類がある・区間を持つ・パラメータを持つ帯 `{ id, kind, ms, len, intro, outro, params }`。
 * **描くのは Godot だけ**（種類の一覧と意味は `client/godot/render/bands/band_kinds.gd`）。Web は文書を**落とさず往復させる**だけで、
 * タイムラインには出さない・UI も持たない。サーバー（`ocg/fx_docs.py::validate_bands`）が形と値域を厳密に見るので、
 * ここでは「形が壊れているものを落とす」までにとどめる（知らない種類は落とさない＝Godot の新しい種類を Web が消さないため）。
 */

const isFin = Number.isFinite;
const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const ID = /^[A-Za-z0-9_-]{1,32}$/;
const NAME = /^[A-Za-z_][A-Za-z0-9_]{0,39}$/;

/** 整数の ms（0 以上。小数は丸める） */
const ms = (v) => (isFin(v) ? Math.max(0, Math.round(v)) : null);

/** params: 平らな数・文字・真偽だけ（それ以外の値と名前の形が違うものは落とす） */
function cleanParams(value) {
  const out = {};
  if (!isObj(value)) return out;
  for (const [k, v] of Object.entries(value)) {
    if (!NAME.test(k)) continue;
    if (typeof v === 'boolean' || typeof v === 'string' || isFin(v)) out[k] = v;
  }
  return out;
}

/** 帯 1 本を整える（壊れていれば null） */
export function cleanBand(value) {
  if (!isObj(value)) return null;
  if (typeof value.id !== 'string' || !ID.test(value.id)) return null;
  if (typeof value.kind !== 'string' || !value.kind) return null;
  const at = ms(value.ms);
  const len = ms(value.len);
  const intro = ms(value.intro ?? 0);
  const outro = ms(value.outro ?? 0);
  if (at == null || len == null || len < 1 || intro == null || outro == null || intro + outro > len) return null;
  return { id: value.id, kind: value.kind, ms: at, len, intro, outro, params: cleanParams(value.params) };
}

/** 帯の配列を整える（id の重複は後のものを落とす。`ms` の昇順、同じ時刻は元の並び） */
export function cleanBands(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  value.forEach((raw, order) => {
    const b = cleanBand(raw);
    if (!b || seen.has(b.id)) return;
    seen.add(b.id);
    out.push({ ...b, order });
  });
  out.sort((a, b) => a.ms - b.ms || a.order - b.order);
  return out.map(({ order, ...b }) => b);
}
